import { TASK_TYPE_INFO, TASK_TYPES, type TaskType } from "@krut/shared";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { keepPreviousData, useGroups } from "../hooks.ts";
import { relative } from "../lib/cron.ts";
import { Button, Card, cx, ErrorText, Input, PageHeader, Select, Spinner } from "../components/ui.tsx";
import { browserZone, fmtHours, fmtMinutes, typeLabel } from "../components/TimeSaved.tsx";

/**
 * Admin → Workspace → Hours saved: the time assistants saved each person, counted from the tasks they
 * finished (each type's standard time, or the person's own answer), from the day the first one was recorded.
 */

const PERIODS = [
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
  { id: "all", label: "Since start" },
] as const;
type PeriodId = (typeof PERIODS)[number]["id"];

const fetchSummary = (period: PeriodId, group: string) =>
  unwrap(api.admin["time-saved"].$get({ query: { period, tz: browserZone(), ...(group && { group }) } }));
type Summary = Awaited<ReturnType<typeof fetchSummary>>;

/** YYYY-MM-DD as a local calendar day (no time zone shift). */
const day = (ymd: string) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y!, m! - 1, d!);
};
const shortDay = (ymd: string) => day(ymd).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export function HoursSavedPage() {
  const [period, setPeriod] = useState<PeriodId>("30d");
  const [group, setGroup] = useState("");
  const groups = useGroups();
  const q = useQuery({ queryKey: ["time-saved", period, group], queryFn: () => fetchSummary(period, group), placeholderData: keepPreviousData });
  const d = q.data;

  return (
    <>
      <PageHeader
        title="Hours saved"
        description={
          d?.since
            ? `Time assistants saved people, from the tasks they finished. Counted since ${day(d.since.slice(0, 10)).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}.`
            : "Time assistants save people, counted from the tasks they finish."
        }
        actions={
          <>
            <div role="group" aria-label="Period" className="flex rounded-lg border border-[var(--line)] p-0.5 text-xs">
              {PERIODS.map((p) => (
                <button
                  key={p.id}
                  aria-pressed={period === p.id}
                  onClick={() => setPeriod(p.id)}
                  className={cx("whitespace-nowrap rounded-md px-2.5 py-1.5", period === p.id ? "bg-[var(--panel-2)] font-medium" : "text-[var(--muted)] hover:text-[var(--text)]")}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Select aria-label="Group" value={group} onChange={(e) => setGroup(e.target.value)} className="w-44">
              <option value="">All groups</option>
              {groups.data?.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </Select>
            <Button disabled={!d?.people.length} onClick={() => d && downloadCsv(d)}>Export CSV</Button>
          </>
        }
      />
      {q.isPending && <div className="grid place-items-center py-16"><Spinner /></div>}
      <ErrorText error={q.error} />
      {d && (
        <div className={cx("transition-opacity", q.isPlaceholderData && "opacity-60")}>
          {!d.since && (
            <Card className="mb-6 p-5 text-sm text-[var(--muted)]">
              Nothing recorded yet. From now on, when an assistant finishes a piece of work for someone (a draft, research, a spreadsheet, code), it
              records the type of work, and the numbers start here.
            </Card>
          )}
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi label="Hours saved" value={fmtHours(d.total.minutes)} />
            <Kpi label="Tasks finished" value={d.total.tasks.toLocaleString()} />
            <Kpi label="People it saved time" value={`${d.total.people} of ${d.people.filter((p) => p.active).length}`} />
            <Kpi label="Tasks people rated" value={d.total.tasks ? `${Math.round((d.total.answered / d.total.tasks) * 100)}%` : "—"} />
          </div>

          <div className="mb-6 grid gap-6 lg:grid-cols-3">
            <Card className="p-5 lg:col-span-2">
              <SeriesChart series={d.series} unit={d.unit} />
            </Card>
            <Card className="p-5">
              <h2 className="mb-1 text-sm font-semibold">By type of work</h2>
              <p className="mb-4 text-xs text-[var(--muted)]">Hours saved in this period</p>
              <TypeBars rows={d.byType} />
            </Card>
          </div>

          <Card className="mb-6 overflow-x-auto">
            <PeopleTable key={`${period}:${group}`} people={d.people} />
          </Card>

          <StandardTimes standard={d.standard} answers={d.answers} />
        </div>
      )}
    </>
  );
}

function Kpi({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-[var(--muted)]">{label}</div>
      <div className="mt-1.5 font-display text-[22px] leading-tight font-medium tabular-nums">{value}</div>
    </Card>
  );
}

/** One measure (hours saved) per day or week: bars from the baseline, a tooltip per bar, and a table for screen readers. */
function SeriesChart({ series, unit }: { series: Summary["series"]; unit: Summary["unit"] }) {
  const [hover, setHover] = useState<number | null>(null);
  const max = Math.max(...series.map((s) => s.minutes), 1);
  const label = (b: string) => (unit === "week" ? `Week of ${shortDay(b)}` : day(b).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }));
  const h = hover !== null ? series[hover] : null;
  const empty = series.every((s) => s.tasks === 0);
  return (
    <div>
      <h2 className="text-sm font-semibold">Hours saved per {unit}</h2>
      <p className="mb-4 text-xs text-[var(--muted)]">{empty ? "No tasks in this period." : `Highest ${unit}: ${fmtHours(max)}`}</p>
      <div className="relative">
        {h && hover !== null && (
          <div
            className="pointer-events-none absolute -top-2 z-10 whitespace-nowrap rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2.5 py-1.5 text-xs shadow-md"
            style={{
              left: `${((hover + 0.5) / series.length) * 100}%`,
              transform: `translate(${hover < series.length * 0.2 ? "-10%" : hover > series.length * 0.8 ? "-90%" : "-50%"}, -100%)`,
            }}
          >
            <div className="font-medium">{label(h.bucket)}</div>
            <div className="text-[var(--muted)]">
              {fmtHours(h.minutes)} saved · {h.tasks} task{h.tasks === 1 ? "" : "s"}
            </div>
          </div>
        )}
        <div className="flex h-48 items-end gap-[2px] border-b border-[var(--line)]" onMouseLeave={() => setHover(null)} aria-hidden>
          {series.map((s, i) => (
            <div key={s.bucket} className="flex h-full flex-1 items-end" onMouseEnter={() => setHover(i)}>
              <div
                className={cx("w-full rounded-t-[4px] transition-colors", hover === i ? "bg-[var(--color-accent)]" : "bg-[var(--color-accent)]/70")}
                style={{ height: s.minutes ? `${Math.max(2, (s.minutes / max) * 100)}%` : 0 }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-1.5 flex justify-between text-[11px] text-[var(--muted)]">
        <span>{series[0] ? (unit === "week" ? `Week of ${shortDay(series[0].bucket)}` : shortDay(series[0].bucket)) : ""}</span>
        <span>{unit === "week" ? "This week" : "Today"}</span>
      </div>
      <table className="sr-only">
        <caption>Hours saved per {unit}</caption>
        <thead>
          <tr><th>{unit === "week" ? "Week of" : "Day"}</th><th>Hours saved</th><th>Tasks</th></tr>
        </thead>
        <tbody>
          {series.map((s) => (
            <tr key={s.bucket}><td>{label(s.bucket)}</td><td>{fmtHours(s.minutes)}</td><td>{s.tasks}</td></tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** People without tasks in the period wait behind a "show" row when there are more than this many. */
const IDLE_SHOWN = 3;

function PeopleTable({ people }: { people: Summary["people"] }) {
  const [showIdle, setShowIdle] = useState(false);
  const max = Math.max(1, ...people.map((p) => p.minutes));
  const idle = people.filter((p) => !p.tasks).length;
  const hideIdle = idle > IDLE_SHOWN && !showIdle;
  const shown = hideIdle ? people.filter((p) => p.tasks) : people;
  return (
    <table className="w-full min-w-[720px] text-sm">
      <thead>
        <tr className="whitespace-nowrap border-b border-[var(--line)] text-left text-xs uppercase tracking-wider text-[var(--muted)]">
          <th className="px-4 py-3 font-medium">Person</th>
          <th className="px-4 py-3 font-medium">Hours saved</th>
          <th className="px-4 py-3 font-medium">Tasks</th>
          <th className="px-4 py-3 font-medium" title="Tasks the person said how much time they saved">Rated</th>
          <th className="px-4 py-3 font-medium">Mostly</th>
          <th className="px-4 py-3 font-medium">Last task</th>
        </tr>
      </thead>
      <tbody>
        {!people.length && <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--muted)]">Nobody in this group.</td></tr>}
        {shown.map((p) => (
          <tr key={p.userId} className={cx("border-b border-[var(--line)] last:border-0", !p.tasks && "text-[var(--muted)]")}>
            <td className="px-4 py-3">
              <div className="font-medium">{p.name}</div>
              <div className="text-xs text-[var(--muted)]">{p.email} · {p.groupName ?? "No group"}{!p.active && " · disabled"}</div>
            </td>
            <td className="w-48 px-4 py-3">
              {p.tasks ? (
                <>
                  <div className="text-sm font-medium">{fmtHours(p.minutes)}</div>
                  <div className="mt-1 h-1.5 rounded-r-[4px] bg-[var(--panel-2)]">
                    <div className="h-full rounded-r-[4px] bg-[var(--color-accent)]/80" style={{ width: `${(p.minutes / max) * 100}%` }} />
                  </div>
                </>
              ) : (
                "—"
              )}
            </td>
            <td className="px-4 py-3">{p.tasks || "—"}</td>
            <td className="px-4 py-3 text-[var(--muted)]">{p.tasks ? `${p.answered} of ${p.tasks}` : "—"}</td>
            <td className="px-4 py-3">{typeLabel(p.topType)}</td>
            <td className="px-4 py-3 text-xs text-[var(--muted)]">{p.lastAt ? relative(p.lastAt) : "—"}</td>
          </tr>
        ))}
        {idle > IDLE_SHOWN && (
          <tr>
            <td colSpan={6} className="px-4 py-3 text-center">
              <button onClick={() => setShowIdle(!showIdle)} className="text-xs font-medium text-[var(--color-accent)] hover:underline">
                {showIdle ? "Hide people without tasks" : `Show ${idle} ${idle === 1 ? "person" : "people"} without tasks in this period`}
              </button>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

function TypeBars({ rows }: { rows: Summary["byType"] }) {
  const shown = rows.filter((r) => r.tasks > 0);
  if (!shown.length) return <p className="text-sm text-[var(--muted)]">No tasks in this period.</p>;
  const max = Math.max(1, ...shown.map((r) => r.minutes));
  return (
    <ul className="space-y-3">
      {shown.map((r) => (
        <li key={r.type} title={`${typeLabel(r.type)}: ${fmtHours(r.minutes)} from ${r.tasks} task${r.tasks === 1 ? "" : "s"}`}>
          <div className="mb-1 flex justify-between gap-2 text-xs">
            <span>{typeLabel(r.type)}</span>
            <span className="shrink-0 text-[var(--muted)]">{fmtHours(r.minutes)} · {r.tasks} task{r.tasks === 1 ? "" : "s"}</span>
          </div>
          <div className="h-2 rounded-r-[4px] bg-[var(--panel-2)]">
            <div className="h-full rounded-r-[4px] bg-[var(--color-accent)]/80" style={{ width: `${(r.minutes / max) * 100}%` }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

/** People's answers suggest a new standard time once there are enough of them and they differ noticeably. */
const MIN_ANSWERS = 5;

function StandardTimes({ standard, answers }: { standard: Summary["standard"]; answers: Summary["answers"] }) {
  const qc = useQueryClient();
  // Only a saved change resets the form (not every refetch, which brings an equal but new object).
  const savedKey = TASK_TYPES.map((t) => standard[t]).join(",");
  const saved = useMemo(() => toDraft(standard), [savedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [draft, setDraft] = useState<Record<TaskType, string>>(saved);
  useEffect(() => setDraft(saved), [saved]);
  const minutes = Object.fromEntries(TASK_TYPES.map((t) => [t, Number(draft[t])])) as Record<TaskType, number>;
  const valid = TASK_TYPES.every((t) => draft[t] !== "" && Number.isInteger(minutes[t]) && minutes[t] >= 0 && minutes[t] <= 480);
  const changed = TASK_TYPES.some((t) => draft[t] !== saved[t]);
  const save = useMutation({
    mutationFn: () => unwrap(api.admin["time-saved"].standard.$put({ json: { minutes } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["time-saved"] }),
  });
  return (
    <Card className="p-5">
      <h2 className="text-sm font-semibold">Standard times</h2>
      <p className="mt-1 mb-4 text-xs text-[var(--muted)]">
        How long each type of task would take someone by hand. A task counts its type's time unless the person said how much it saved them.
        A change recounts every task, earlier ones too.
      </p>
      <div className="divide-y divide-[var(--line)]">
        {TASK_TYPES.map((t) => {
          const a = answers.find((x) => x.type === t);
          // Rounded to 5 minutes; offered once it differs from the time in the box by at least that.
          const avg = a ? Math.min(480, Math.round(a.avgMinutes / 5) * 5) : null;
          const suggest = a && avg !== null && a.answered >= MIN_ANSWERS && avg !== minutes[t] ? avg : null;
          return (
            <div key={t} className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3">
              <div className="min-w-48 flex-1">
                <div className="text-sm font-medium">{TASK_TYPE_INFO[t].label}</div>
                <div className="text-xs text-[var(--muted)]">{TASK_TYPE_INFO[t].covers}</div>
              </div>
              <div className="w-56 text-xs text-[var(--muted)]">
                {a ? `People said about ${fmtMinutes(a.avgMinutes)} (${a.answered} answer${a.answered === 1 ? "" : "s"})` : "No answers yet"}
                {suggest !== null && (
                  <button onClick={() => setDraft({ ...draft, [t]: String(suggest) })} className="ml-2 font-medium text-[var(--color-accent)] hover:underline">
                    Use {suggest} min
                  </button>
                )}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Input
                  type="number"
                  min={0}
                  max={480}
                  step={5}
                  value={draft[t]}
                  onChange={(e) => setDraft({ ...draft, [t]: e.target.value })}
                  className="w-20 text-right"
                  aria-label={`${TASK_TYPE_INFO[t].label}: minutes`}
                />
                min
              </label>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button variant="primary" disabled={!changed || !valid || save.isPending} onClick={() => save.mutate()}>
          {save.isPending && <Spinner />}
          Save standard times
        </Button>
        {changed && <Button variant="ghost" onClick={() => setDraft(saved)}>Undo changes</Button>}
        {!valid && <span className="text-xs text-red-700">Each time must be a whole number of minutes, 0 to 480.</span>}
      </div>
      <ErrorText error={save.error} />
    </Card>
  );
}

const toDraft = (s: Summary["standard"]) => Object.fromEntries(TASK_TYPES.map((t) => [t, String(s[t])])) as Record<TaskType, string>;

/** Everyone in the current view, one row each. */
function downloadCsv(d: Summary) {
  // Text that a spreadsheet would run as a formula (=, +, -, @) is kept as text.
  const cell = (v: string | number) => {
    if (typeof v === "number") return String(v);
    const t = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const rows = [
    ["Name", "Email", "Group", "Tasks", "Hours saved", "Tasks rated", "Mostly", "Last task", "From", "To"],
    ...d.people.map((p) => [p.name, p.email, p.groupName ?? "", p.tasks, +(p.minutes / 60).toFixed(2), p.answered, p.topType ? typeLabel(p.topType) : "", p.lastAt ?? "", d.from, d.to]),
  ];
  const blob = new Blob([rows.map((r) => r.map(cell).join(",")).join("\n") + "\n"], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `hours-saved-${d.from}-to-${d.to}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
